import { useState, useMemo, useEffect } from "react";
import {
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
  Control,
  FieldValues,
} from "react-hook-form";
import {
  Plus,
  Trash2,
  Coins,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import Button from "../UI/Button";
import { Select, Input } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CreateShipmentData } from "../../types";
import { CostService } from "../../types/costService";
import { Service } from "../../types/service";
import { ServiceAddInline } from "./ServiceAddInline";
import { useTranslation } from "react-i18next";
import { listHasTax, sumTax, taxCellLabel } from "../../utils/tax";

interface MatrixRowState {
  selectedZoneId: string | null;
  selectedBandId: string | null;
  frozenZoneName: string | null;
  frozenBandLabel: string | null;
  frozenPrice: number | null;
  isHistoric: boolean;
}

/**
 * Shared by the shipment form and the container order form. Both carry an
 * identically-shaped `costServices` array, so the section is generic over the
 * form type and casts to the shipment shape internally for the field paths.
 */
interface CostServicesSectionProps<T extends FieldValues = CreateShipmentData> {
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  readonly: boolean;
  costServiceFields: any[];
  appendCostService: (item: any) => void;
  removeCostService: (index: number) => void;
  totalForEachRow: any[];
  grandTotal: number;
  services: CostService[];
  setValue: UseFormSetValue<T>;
  control: Control<T>;
  /**
   * Post-completion mode: the record this section belongs to is finished and
   * its form is locked, but cost rows may still be managed through dedicated
   * API endpoints. Add + delete only — no inline editing.
   *
   * The billable table has had this since shipments needed it; cost services
   * need it for the same reason one level over, on containers: a supplier's
   * invoice for a lift or a waiting hour lands after the box is back, and the
   * container it belongs to is a closed record by then. Refusing to record what
   * a finished job cost is not bookkeeping, it is a hole in it.
   */
  postCompletionMode?: boolean;
  onApiAddService?: (service: Record<string, unknown>) => Promise<void>;
  onApiDeleteService?: (serviceId: string) => Promise<void>;
}

function getRowServiceStatus(
  serviceName: string,
  services: CostService[],
): "found" | "deleted" | "empty" {
  if (!serviceName) return "empty";
  const found = services.find((s) => s.name === serviceName);
  return found ? "found" : "deleted";
}

export function CostServicesSection<
  T extends FieldValues = CreateShipmentData,
>({
  register: registerProp,
  readonly,
  costServiceFields,
  appendCostService,
  removeCostService,
  totalForEachRow,
  services,
  setValue: setValueProp,
  control: controlProp,
  grandTotal,
  postCompletionMode = false,
  onApiAddService,
  onApiDeleteService,
}: CostServicesSectionProps<T>) {
  const register = registerProp as unknown as UseFormRegister<CreateShipmentData>;
  const setValue = setValueProp as unknown as UseFormSetValue<CreateShipmentData>;
  const control = controlProp as unknown as Control<CreateShipmentData>;

  const { t } = useTranslation("shipments");
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [partnerFilter, setPartnerFilter] = useState<"GENERAL" | string>(
    "GENERAL",
  );
  const [matrixRows, setMatrixRows] = useState<Record<number, MatrixRowState>>(
    {},
  );

  // Post-completion inline add row (API-backed; no form state involved).
  const [showApiAdd, setShowApiAdd] = useState(false);
  const [apiBusy, setApiBusy] = useState(false);

  // In post-completion mode cost rows are managed through dedicated endpoints
  // even though the rest of the form stays readonly.
  const canManage = postCompletionMode && !!onApiAddService;
  const showActions = !readonly || canManage;

  // The Tax column is always shown (rows without a VAT category render "—").
  // The tax totals in the footer only appear when at least one row carries a
  // VAT category (i.e. anything other than NO_TAX).
  const hasAnyTax = listHasTax(totalForEachRow);
  const grandTax = sumTax(totalForEachRow);

  const availablePartners = useMemo(() => {
    const seen = new Map<string, string>();
    services.forEach((s) => {
      if (
        s.partnerId &&
        s.partnerInfo?.name &&
        !seen.has(s.partnerId) &&
        !s.partnerInfo.isDeleted
      ) {
        seen.set(s.partnerId, s.partnerInfo.name);
      }
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [services]);

  const visibleServices = useMemo(() => {
    if (partnerFilter === "GENERAL") {
      return services.filter((s) => s.partnerId == null);
    }
    return services.filter((s) => s.partnerId === partnerFilter);
  }, [services, partnerFilter]);

  useEffect(() => {
    costServiceFields.forEach((_, index) => {
      const rowValues = (control._formValues as any)?.costServices?.[index];
      if (!rowValues) return;
      if (rowValues.type !== "MATRIX") return;
      if (matrixRows[index]) return;
      const mc = rowValues.matrixContext;
      if (!mc) return;

      const svc = services.find((s) => s.name === rowValues.name);
      const zoneStillExists =
        svc?.zones?.some((z) => z._id === mc.selectedZoneId) ?? false;
      const bandStillExists =
        svc?.bands?.some((b) => b._id === mc.selectedBandId) ?? false;

      setMatrixRows((prev) => ({
        ...prev,
        [index]: {
          selectedZoneId: zoneStillExists ? mc.selectedZoneId : null,
          selectedBandId: bandStillExists ? mc.selectedBandId : null,
          frozenZoneName: mc.zoneName ?? null,
          frozenBandLabel: mc.bandLabel ?? null,
          frozenPrice: rowValues.price ?? null,
          isHistoric: true,
        },
      }));
    });
  }, [costServiceFields.length]);

  const addService = () => {
    appendCostService({
      name: "",
      type: "HOUR",
      price: 0,
      qty: 1,
      total: 0,
      description: "",
      tax: "NO_TAX",
      partnerId: null,
      matrixContext: null,
    });
    setIsCollapsed(false);
  };

  const handleServiceSelect = (index: number, selectedName: string) => {
    const selectedService = services.find((s) => s.name === selectedName);
    if (!selectedService) return;

    setValue(`costServices.${index}.name` as any, selectedService.name);
    setValue(`costServices.${index}.type` as any, selectedService.type);
    setValue(
      `costServices.${index}.description` as any,
      selectedService.description ?? "",
    );
    setValue(
      `costServices.${index}.tax` as any,
      selectedService.tax ?? "NO_TAX",
    );
    setValue(
      `costServices.${index}.partnerId` as any,
      selectedService.partnerId ?? null,
    );

    if (selectedService.type === "MATRIX") {
      setValue(`costServices.${index}.price` as any, 0);
      setValue(`costServices.${index}.total` as any, 0);
      setValue(`costServices.${index}.matrixContext` as any, undefined);

      setMatrixRows((prev) => ({
        ...prev,
        [index]: {
          selectedZoneId: null,
          selectedBandId: null,
          frozenZoneName: null,
          frozenBandLabel: null,
          frozenPrice: null,
          isHistoric: false,
        },
      }));
    } else {
      setValue(
        `costServices.${index}.price` as any,
        selectedService.price ?? 0,
      );
      const qty = (control._formValues as any)?.costServices?.[index]?.qty || 1;
      setValue(
        `costServices.${index}.total` as any,
        (selectedService.price ?? 0) * qty,
      );
      setValue(`costServices.${index}.matrixContext` as any, undefined);

      setMatrixRows((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    }
  };

  const resolveMatrixPrice = (
    index: number,
    zoneId: string | null,
    bandId: string | null,
    svc: CostService,
  ) => {
    if (!zoneId || !bandId) {
      setValue(`costServices.${index}.price` as any, 0);
      setValue(`costServices.${index}.total` as any, 0);
      setValue(`costServices.${index}.matrixContext` as any, undefined);
      return;
    }

    const cell = svc.priceMatrix?.find(
      (c) => c.zoneId === zoneId && c.bandId === bandId,
    );

    if (!cell) {
      setValue(`costServices.${index}.price` as any, 0);
      setValue(`costServices.${index}.total` as any, 0);
      setValue(`costServices.${index}.matrixContext` as any, undefined);
      return;
    }

    const zone = svc.zones?.find((z) => z._id === zoneId);
    const band = svc.bands?.find((b) => b._id === bandId);
    const qty = (control._formValues as any)?.costServices?.[index]?.qty || 1;

    setValue(`costServices.${index}.price` as any, cell.price);
    setValue(`costServices.${index}.total` as any, cell.price * qty);
    setValue(`costServices.${index}.matrixContext` as any, {
      zoneName: zone?.name ?? "",
      bandLabel: band?.label ?? "",
      selectedZoneId: zoneId,
      selectedBandId: bandId,
    });
  };

  const handleZoneChange = (index: number, zoneId: string) => {
    const serviceName = (control._formValues as any)?.costServices?.[index]
      ?.name;
    const svc = services.find((s) => s.name === serviceName);
    if (!svc) return;
    const newBandId = matrixRows[index]?.selectedBandId ?? null;
    setMatrixRows((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        selectedZoneId: zoneId || null,
        isHistoric: false,
      },
    }));
    resolveMatrixPrice(index, zoneId || null, newBandId, svc);
  };

  const handleBandChange = (index: number, bandId: string) => {
    const serviceName = (control._formValues as any)?.costServices?.[index]
      ?.name;
    const svc = services.find((s) => s.name === serviceName);
    if (!svc) return;
    const newZoneId = matrixRows[index]?.selectedZoneId ?? null;
    setMatrixRows((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        selectedBandId: bandId || null,
        isHistoric: false,
      },
    }));
    resolveMatrixPrice(index, newZoneId, bandId || null, svc);
  };

  // ── Post-completion (API-backed) cost management ─────────────────────────
  const handleApiDelete = async (serviceId: string) => {
    if (!onApiDeleteService) return;
    if (!window.confirm(t("costServices.confirmDelete"))) return;
    setApiBusy(true);
    try {
      await onApiDeleteService(serviceId);
    } finally {
      setApiBusy(false);
    }
  };

  const handleApiSave = async (payload: Record<string, unknown>) => {
    if (!onApiAddService) return;
    setApiBusy(true);
    try {
      await onApiAddService(payload);
      setShowApiAdd(false);
    } finally {
      setApiBusy(false);
    }
  };

  const handleRemoveService = (index: number) => {
    removeCostService(index);
    setMatrixRows((prev) => {
      const next: Record<number, MatrixRowState> = {};
      Object.entries(prev).forEach(([k, v]) => {
        const ki = parseInt(k, 10);
        if (ki < index) next[ki] = v;
        else if (ki > index) next[ki - 1] = v;
      });
      return next;
    });
  };

  // Cells wrap rather than run on. The table is laid out fixed (see below), so
  // a long description takes a second line inside its column instead of pushing
  // the table past the pane and putting a horizontal scrollbar under it.
  // Text cells wrap inside their column; `tdTight` is for the cells holding a
  // single indivisible token (a price, a quantity) that must not be split.
  const td = "px-2 py-1.5 align-top wrap-break-word";
  const tdTight = "px-2 py-1.5 align-top whitespace-nowrap";
  const th =
    "px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wider";

  /**
   * A saved value in read mode, presented rather than shown as a greyed-out
   * form. The billable table's `ReadCell`, for the same reason and so the two
   * tables under one another read the same way.
   */
  const ReadCell = ({ value }: { value?: React.ReactNode }) => {
    const empty =
      value === undefined || value === null || value === "" || value === "—";
    return (
      <span className={`text-[12px] ${empty ? "text-gray-300" : "text-ink"}`}>
        {empty ? "—" : value}
      </span>
    );
  };

  const renderZoneCell = (index: number, locked: boolean = readonly) => {
    const rowValues = (control._formValues as any)?.costServices?.[index];
    if (!rowValues || rowValues.type !== "MATRIX") {
      return <td className={`${td} text-gray-300`}>—</td>;
    }

    const serviceName = rowValues.name;
    const svc = services.find((s) => s.name === serviceName);
    const rowState = matrixRows[index];

    if (!svc) {
      const frozenName =
        rowState?.frozenZoneName ?? rowValues.matrixContext?.zoneName;
      return (
        <td className={td}>
          <div className="flex items-center gap-1">
            <span className="text-[12px] text-gray-600">
              {frozenName || "—"}
            </span>
            <span className="text-[10px] bg-amber-100 text-amber-700 px-1 rounded">
              {t("costServices.historic")}
            </span>
          </div>
        </td>
      );
    }

    const savedZoneId =
      rowState?.selectedZoneId ?? rowValues.matrixContext?.selectedZoneId;
    const savedZoneStillExists =
      savedZoneId && svc.zones?.some((z) => z._id === savedZoneId);
    const hasMismatch =
      rowState?.isHistoric && savedZoneId && !savedZoneStillExists;

    if (hasMismatch) {
      const frozenName =
        rowState?.frozenZoneName ?? rowValues.matrixContext?.zoneName;
      return (
        <td className={td}>
          <div className="flex flex-col gap-0.5">
            <span className="text-[12px] text-amber-600 line-through">
              {frozenName}
            </span>
            <span className="text-[10px] text-red-500">
              {t("costServices.zoneRemoved")}
            </span>
            {/* The re-pick, offered only where there is something to pick
                with. Read mode says what happened and stops there. */}
            {!readonly && (
              <select
                value=""
                onChange={(e) => handleZoneChange(index, e.target.value)}
                disabled={locked}
                className="w-full h-8 px-2.5 py-1 text-[13px] border border-red-400 rounded-md mt-0.5"
              >
                <option value="">{t("costServices.selectZone")}</option>
                {(svc.zones ?? []).map((z) => (
                  <option key={z._id} value={z._id}>
                    {z.name}
                  </option>
                ))}
              </select>
            )}
          </div>
        </td>
      );
    }

    const currentZoneId = rowState?.selectedZoneId ?? null;
    const isRequired = !currentZoneId;

    if (readonly) {
      // The zone as booked. The picker's own value first, the frozen name off
      // the row second — a row read back from the record has the latter and
      // may never have had the former in this session.
      const zoneName =
        svc.zones?.find((z) => z._id === currentZoneId)?.name ??
        rowValues.matrixContext?.zoneName;
      return (
        <td className={td}>
          <ReadCell value={zoneName} />
        </td>
      );
    }

    return (
      <td className={td}>
        <select
          value={currentZoneId ?? ""}
          onChange={(e) => handleZoneChange(index, e.target.value)}
          disabled={locked}
          className={`w-full h-8 px-2.5 py-1 text-[13px] border rounded-md ${
            isRequired ? "border-red-400 " : "border-gray-300 "
          }`}
        >
          <option value="">{t("costServices.selectZone")}</option>
          {(svc.zones ?? []).map((z) => (
            <option key={z._id} value={z._id}>
              {z.name}
            </option>
          ))}
        </select>
      </td>
    );
  };

  const renderBandCell = (index: number, locked: boolean = readonly) => {
    const rowValues = (control._formValues as any)?.costServices?.[index];
    if (!rowValues || rowValues.type !== "MATRIX") {
      return <td className={`${td} text-gray-300`}>—</td>;
    }

    const serviceName = rowValues.name;
    const svc = services.find((s) => s.name === serviceName);
    const rowState = matrixRows[index];

    if (!svc) {
      const frozenLabel =
        rowState?.frozenBandLabel ?? rowValues.matrixContext?.bandLabel;
      return (
        <td className={td}>
          <div className="flex items-center gap-1">
            <span className="text-[12px] text-gray-600">
              {frozenLabel || "—"}
            </span>
            <span className="text-[10px] bg-amber-100 text-amber-700 px-1 rounded">
              {t("costServices.historic")}
            </span>
          </div>
        </td>
      );
    }

    const savedBandId =
      rowState?.selectedBandId ?? rowValues.matrixContext?.selectedBandId;
    const savedBandStillExists =
      savedBandId && svc.bands?.some((b) => b._id === savedBandId);
    const hasMismatch =
      rowState?.isHistoric && savedBandId && !savedBandStillExists;

    if (hasMismatch) {
      const frozenLabel =
        rowState?.frozenBandLabel ?? rowValues.matrixContext?.bandLabel;
      return (
        <td className={td}>
          <div className="flex flex-col gap-0.5">
            <span className="text-[12px] text-amber-600 line-through">
              {frozenLabel}
            </span>
            <span className="text-[10px] text-red-500">
              {t("costServices.bandRemoved")}
            </span>
            {/* The re-pick, offered only where there is something to pick
                with. Read mode says what happened and stops there. */}
            {!readonly && (
              <select
                value=""
                onChange={(e) => handleBandChange(index, e.target.value)}
                disabled={locked}
                className="w-full h-8 px-2.5 py-1 text-[13px] border border-red-400 rounded-md mt-0.5"
              >
                <option value="">{t("costServices.selectBand")}</option>
                {(svc.bands ?? []).map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </td>
      );
    }

    const currentBandId = rowState?.selectedBandId ?? null;
    const isRequired = !currentBandId;

    if (readonly) {
      const bandLabel =
        svc.bands?.find((b) => b._id === currentBandId)?.label ??
        rowValues.matrixContext?.bandLabel;
      return (
        <td className={td}>
          <ReadCell value={bandLabel} />
        </td>
      );
    }

    return (
      <td className={td}>
        <select
          value={currentBandId ?? ""}
          onChange={(e) => handleBandChange(index, e.target.value)}
          disabled={locked}
          className={`w-full h-8 px-2.5 py-1 text-[13px] border rounded-md ${
            isRequired ? "border-red-400 " : "border-gray-300 "
          }`}
        >
          <option value="">{t("costServices.selectBand")}</option>
          {(svc.bands ?? []).map((b) => (
            <option key={b._id} value={b._id}>
              {b.label}
            </option>
          ))}
        </select>
      </td>
    );
  };

  return (
    <SectionCard
      icon={Coins}
      title={t("costServices.title")}
      collapsible
      onToggle={() => setIsCollapsed((p) => !p)}
      actions={
        <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
          {(!readonly || canManage) && !isCollapsed && (
            <>
              <select
                value={partnerFilter}
                onChange={(e) => setPartnerFilter(e.target.value)}
                className="h-8 max-w-45 min-w-0 rounded-md border border-gray-300 bg-white px-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="GENERAL">
                  {t("costServices.generalFilter")}
                </option>
                {availablePartners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                onClick={() => {
                  // Locked form, open charges: the row is added through the API
                  // rather than pushed into a form that is not going to be
                  // saved.
                  if (canManage) {
                    setShowApiAdd(true);
                    setIsCollapsed(false);
                  } else {
                    addService();
                  }
                }}
                variant="primary"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                {t("costServices.addService")}
              </Button>
            </>
          )}
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      }
    >
      {isCollapsed ? (
        costServiceFields.length > 0 ? (
          <p className="text-[12px] text-gray-500">
            {costServiceFields.length}{" "}
            {costServiceFields.length === 1
              ? t("costServices.serviceCount_one", {
                  defaultValue: "cost service",
                })
              : t("costServices.serviceCount_other", {
                  defaultValue: "cost services",
                })}{" "}
            · € {grandTotal}
          </p>
        ) : null
      ) : (
        <>
          {costServiceFields.length > 0 && (
            <div className="border border-gray-200 rounded-md overflow-hidden">
              {/* The billable table's scheme exactly — see the long note there.
                  Two columns fewer, so the readable floor is 840px rather than
                  960 and this table reaches it sooner than the one above it.

                  No "By" column, in any module. It separates an office charge
                  from a driver's, and no driver in the product can record a
                  cost: the one endpoint that stamps `addedBy: "driver"` writes
                  a *shipment's* billable services. A column whose every cell is
                  the same badge is a legend, not data. */}
              <div className="overflow-x-auto">
                <table className="w-full min-w-[840px] table-fixed">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {/* A code, not a sentence — see the billable table. */}
                      <th className={`${th} w-[11%]`}>
                        {t("costServices.serviceCode")}
                      </th>
                      <th className={`${th} w-[22%]`}>
                        {t("costServices.description")}
                      </th>
                      <th className={`${th} w-[8%]`}>
                        {t("costServices.unitType")}
                      </th>
                      <th className={`${th} w-[11%]`}>
                        {t("costServices.zones")}
                      </th>
                      <th className={`${th} w-[11%]`}>
                        {t("costServices.bands")}
                      </th>
                      <th className={`${th} w-[8%]`}>
                        {t("costServices.price")}
                      </th>
                      <th className={`${th} w-[5%]`}>
                        {t("costServices.qty")}
                      </th>
                      <th className={`${th} w-[10%]`}>
                        {t("costServices.tax")}
                      </th>
                      <th className={`${th} w-[9%]`}>
                        {t("costServices.totalExcludingTax")}
                      </th>
                      {showActions && (
                        <th className={`${th} w-[5%]`}>
                          {t("costServices.action")}
                        </th>
                      )}
                    </tr>
                  </thead>

                  <tbody className="bg-white">
                    {costServiceFields.map((field, index) => {
                      const rowValues = (control._formValues as any)
                        ?.costServices?.[index];
                      const isMatrix = rowValues?.type === "MATRIX";
                      const serviceStatus = getRowServiceStatus(
                        rowValues?.name ?? "",
                        services,
                      );
                      const rowLocked = readonly;
                      // The stored subdoc id, which is what the delete endpoint
                      // names. Absent on a row that only exists in the form.
                      const rowServiceId: string | undefined =
                        rowValues?._id ?? field._id;
                      // Net (price × qty), the row's tax category, and the gross
                      // total (net + tax). Total shows gross when tax applies.
                      const rowNet =
                        totalForEachRow[index]?.total ??
                        (field.price || 0) * (field.qty || 0);
                      const rowTax = totalForEachRow[index]?.tax ?? field.tax;

                      return (
                        <tr
                          key={field.id || index}
                          className="border-b border-gray-100 last:border-0"
                        >
                          <td className={td}>
                            {readonly ? (
                              <div>
                                <ReadCell value={rowValues?.name?.trim()} />
                                {serviceStatus === "deleted" &&
                                  rowValues?.name?.trim() && (
                                    <span className="text-[10px] text-amber-600 mt-0.5 block">
                                      {t("costServices.serviceDeleted")}
                                    </span>
                                  )}
                              </div>
                            ) : (() => {
                              const selectedName = rowValues?.name?.trim();
                              const serviceExists = selectedName
                                ? visibleServices.some(
                                    (s) => s.name === selectedName,
                                  )
                                : false;

                              const allOptionsForRow =
                                selectedName &&
                                !services.some((s) => s.name === selectedName)
                                  ? [
                                      ...visibleServices,
                                      {
                                        name: selectedName,
                                        type: rowValues?.type ?? "",
                                        price: 0,
                                      },
                                    ]
                                  : !serviceExists && selectedName
                                    ? [
                                        ...visibleServices,
                                        {
                                          name: selectedName,
                                          type: rowValues?.type ?? "",
                                          price: 0,
                                        },
                                      ]
                                    : visibleServices;

                              return (
                                <div>
                                  <Select
                                    {...register(
                                      `costServices.${index}.name` as any,
                                    )}
                                    options={allOptionsForRow.map((s) => ({
                                      label: s.name,
                                      value: s.name,
                                    }))}
                                    disabled={rowLocked}
                                    size="sm"
                                    placeholder={t(
                                      "costServices.selectService",
                                    )}
                                    value={selectedName || ""}
                                    onChange={(e) =>
                                      handleServiceSelect(index, e.target.value)
                                    }
                                    // No max-width: the column is a share of
                                    // the card now, and a select capped at
                                    // 220px inside it just leaves a gap.
                                    className="w-full"
                                    style={{ minWidth: 0 }}
                                  />
                                  {serviceStatus === "deleted" &&
                                    selectedName && (
                                      <span className="text-[10px] text-amber-600 mt-0.5 block">
                                        {t("costServices.serviceDeleted")}
                                      </span>
                                    )}
                                </div>
                              );
                            })()}
                          </td>

                          <td className={td}>
                            {readonly ? (
                              <ReadCell value={rowValues?.description} />
                            ) : (
                              <Input
                                {...register(
                                  `costServices.${index}.description` as any,
                                )}
                                type="text"
                                readOnly={rowLocked}
                                size="sm"
                                placeholder={t(
                                  "costServices.enterDescription",
                                )}
                              />
                            )}
                          </td>

                          <td className={`${tdTight} text-[12px]`}>
                            {rowValues?.type || "–"}
                          </td>

                          {renderZoneCell(index, rowLocked)}
                          {renderBandCell(index, rowLocked)}

                          <td className={tdTight}>
                            {isMatrix || readonly ? (
                              <span className="text-[12px] font-medium text-gray-700">
                                {rowValues?.price
                                  ? `€${Number(rowValues.price).toFixed(2)}`
                                  : "—"}
                              </span>
                            ) : (
                              <Input
                                type="text"
                                inputMode="decimal"
                                placeholder="0.00"
                                size="sm"
                                {...register(
                                  `costServices.${index}.price` as any,
                                  {
                                    validate: (value: any) => {
                                      if (value === undefined)
                                        return t("costServices.priceRequired");
                                      const regex = /^\d{1,5}(\.\d{1,2})?$/;
                                      return regex.test(value.toString())
                                        ? true
                                        : t("costServices.priceFormat");
                                    },
                                    onChange: (e: any) => {
                                      let input = e.target.value;
                                      if (
                                        !/^\d{0,5}(\.\d{0,2})?$/.test(input)
                                      ) {
                                        input = input.slice(0, -1);
                                      }
                                      const price = parseFloat(input) || 0;
                                      const qty =
                                        (control._formValues as any)
                                          ?.costServices?.[index]?.qty || 1;
                                      setValue(
                                        `costServices.${index}.price` as any,
                                        input,
                                      );
                                      setValue(
                                        `costServices.${index}.total` as any,
                                        price * qty,
                                      );
                                    },
                                  },
                                )}
                                disabled={rowLocked}
                                className="text-right"
                              />
                            )}
                          </td>

                          <td className={tdTight}>
                            {readonly ? (
                              <ReadCell value={rowValues?.qty} />
                            ) : (
                            <Input
                              type="text"
                              inputMode="numeric"
                              placeholder="0"
                              size="sm"
                              {...register(`costServices.${index}.qty` as any, {
                                validate: (value: any) => {
                                  if (value === undefined)
                                    return t("costServices.qtyRequired");
                                  const regex = /^\d{1,5}$/;
                                  return regex.test(value.toString())
                                    ? true
                                    : t("costServices.qtyFormat");
                                },
                                onChange: (e: any) => {
                                  let input = e.target.value;
                                  if (input === "") {
                                    setValue(
                                      `costServices.${index}.qty` as any,
                                      1,
                                    );
                                    setValue(
                                      `costServices.${index}.total` as any,
                                      0,
                                    );
                                    return;
                                  }
                                  if (!/^\d{0,5}$/.test(input)) {
                                    input = input.slice(0, -1);
                                  }
                                  const qty = parseInt(input) || 0;
                                  const price = parseFloat(
                                    (control._formValues as any)?.costServices?.[
                                      index
                                    ]?.price || 0,
                                  );
                                  setValue(
                                    `costServices.${index}.qty` as any,
                                    input,
                                  );
                                  setValue(
                                    `costServices.${index}.total` as any,
                                    price * qty,
                                  );
                                },
                              })}
                              disabled={rowLocked}
                              className="text-right"
                            />
                            )}
                          </td>

                          <td className={`${td} text-[12px] text-gray-600`}>
                            {taxCellLabel(rowNet, rowTax)}
                          </td>

                          <td className={`${tdTight} text-[12px] font-medium`}>
                            €{rowNet.toFixed(2)}
                          </td>

                          {showActions && (
                            <td className={td}>
                              {canManage ? (
                                // A stored row can be deleted through the API;
                                // one that only exists in the form cannot, and
                                // in this mode there are none.
                                rowServiceId ? (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    disabled={apiBusy}
                                    onClick={() => handleApiDelete(rowServiceId)}
                                    className="text-red-600 hover:text-red-700 p-1"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                ) : null
                              ) : (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  onClick={() => handleRemoveService(index)}
                                  className="text-red-600 hover:text-red-700 p-1"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center text-[12px]">
                <span className="font-semibold text-gray-600 uppercase tracking-wide">
                  {t("costServices.totals")}
                </span>
                <div className="flex items-center gap-4">
                  <div>
                    <span className="text-gray-500">
                      {t("costServices.totalPrice")}
                    </span>{" "}
                    <span className="font-semibold text-gray-900">
                      € {grandTotal}
                    </span>
                  </div>
                  {hasAnyTax && (
                    <>
                      <div>
                        <span className="text-gray-500">
                          {t("costServices.taxTotal")}
                        </span>{" "}
                        <span className="font-semibold text-gray-900">
                          € {grandTax.toFixed(2)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">
                          {t("costServices.totalWithTax")}
                        </span>{" "}
                        <span className="font-semibold text-gray-900">
                          € {(grandTotal + grandTax).toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

        {/* Post-completion: inline API-backed "add cost" row. Same control the
            billable table uses, so a late supplier charge is recorded the same
            way a late billable one is. */}
        {canManage && showApiAdd && (
          <ServiceAddInline
            services={visibleServices as unknown as Service[]}
            busy={apiBusy}
            onSave={handleApiSave}
            onCancel={() => setShowApiAdd(false)}
          />
        )}
        </>
      )}
    </SectionCard>
  );
}
